import {
    useSignal,
    useSignalEffect,
    useSignals,
} from '@preact/signals-react/runtime'
import {Dropdown} from 'primereact/dropdown'
import {forwardRef, useCallback, useImperativeHandle, useMemo} from 'react'
import TranslateUtils from '../../../i18n/TranslateUtils'
import {component} from '../../form/Constant'
import {isArrayAndNotEmpty} from '../../observations/Utils'

// Pass OnChange whenever you want to get the newest selected value
// Call the ref function getValue() to get the selected value

const StyledDropdown = forwardRef(
    (
        {
            key = '',
            options = [],
            value = null,
            placeholder = '',
            onChange = () => {},
            hasPanelFooter = false,
            optionType = component.dropdown,
            ...restProps
        },
        ref,
    ) => {
        useSignals()

        useImperativeHandle(ref, () => ({
            getValue: () => handleGetValue(),
            changeValue: newValue => handleChangeValue(newValue),
        }))

        const initialValue = useMemo(() => {
            return value || null
        }, [value])

        //#region Hooks
        const {translateCommons, translateKeys} = TranslateUtils()
        const {translateOptions} = TranslateUtils()
        //#endregion

        //#region Local State
        const panelVisible = useSignal(false)
        const selectedValue = useSignal(initialValue)

        const handleGetValue = () => {
            return selectedValue.peek()
        }

        const handleChangeValue = newValue => {
            selectedValue.value = newValue
        }

        const handleGetSelectedLabel = selectedValue => {
            let tmpOptions = []

            if (optionType === component.dropdownGroup) {
                tmpOptions = options?.flatMap(item => item.items)
            } else {
                tmpOptions = options
            }

            const selectedItem = tmpOptions?.find(
                item => item.value === selectedValue,
            )
            return selectedItem?.label
        }
        //#endregion

        //#region Footer
        const appendFooter = () => {
            if (!document.querySelector('.p-dropdown-footer')) {
                var footer = document.createElement('div')
                footer.className = 'p-dropdown-footer'
                footer.setAttribute('data-pc-section', 'footer')
                footer.innerHTML = `
                <div class="p-3" style="background-color: rgb(249, 249, 249); font-weight: bold;">
                  ${
                      selectedValue.peek()
                          ? `<span><span class="text-darkcyan">${handleGetSelectedLabel(
                                selectedValue.peek(),
                            )}</span> is being</span>`
                          : 'No item'
                  } selected.
                </div>`

                var dropdownWrapper = document.querySelector(
                    '.p-dropdown-items-wrapper',
                )
                if (dropdownWrapper) {
                    dropdownWrapper.parentNode.insertBefore(
                        footer,
                        dropdownWrapper.nextSibling,
                    )
                }
            }
        }
        const removeFooter = () => {
            var footer = document.querySelector('.p-dropdown-footer')
            if (footer) {
                footer.parentNode.removeChild(footer)
            }
        }
        //#endregion

        //#region Prevent Scroll Helper
        useSignalEffect(() => {
            const contentDiv = document.querySelector(
                '#blockscroll-sidebar .p-sidebar-content',
            )

            toggleBlockScroll(contentDiv, panelVisible.value)

            return () => {
                if (!!contentDiv) {
                    contentDiv.removeEventListener('wheel', preventScroll)
                    contentDiv.removeEventListener('touchmove', preventScroll)
                }
            }
        })

        const preventScroll = event => {
            event.preventDefault()
            event.stopPropagation()
        }

        const toggleBlockScroll = useCallback((contentDiv, isVisible) => {
            if (!!contentDiv) {
                if (!!isVisible) {
                    contentDiv.addEventListener('wheel', preventScroll, {
                        passive: false,
                    })
                    contentDiv.addEventListener('touchmove', preventScroll, {
                        passive: false,
                    })
                } else {
                    contentDiv.removeEventListener('wheel', preventScroll)
                    contentDiv.removeEventListener('touchmove', preventScroll)
                }
            }
        }, [])
        //#endregion

        return (
            <div id="styled-multi-select">
                <Dropdown
                    key={key}
                    className="w-full h-3rem"
                    style={{fontSize: '14px'}}
                    options={options?.length > 0 ? options : []}
                    value={selectedValue.value || null}
                    onChange={e => {
                        if (onChange) {
                            onChange(e.value)
                        }

                        selectedValue.value = e.value
                    }}
                    placeholder={translateCommons(placeholder)}
                    optionLabel="label"
                    optionValue="value"
                    emptyFilterMessage={translateCommons('No results found')}
                    display={
                        isArrayAndNotEmpty(value) && value?.length < 4
                            ? 'chip'
                            : 'comma'
                    }
                    filter
                    filterBy="label"
                    filterPlaceholder={translateCommons('Search here')}
                    resetFilterOnHide={true}
                    showClear={false}
                    maxLength={3}
                    onShow={() => {
                        if (hasPanelFooter) {
                            appendFooter()
                        }
                        panelVisible.value = true
                    }}
                    onHide={() => {
                        if (hasPanelFooter) {
                            removeFooter()
                        }

                        panelVisible.value = false
                    }}
                    {...restProps}
                />
            </div>
        )
    },
)

export default StyledDropdown
